import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Wheat, FileDown, Search, Thermometer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { api } from "@/lib/api";
import { formatDateEU, todayISO } from "@/lib/constants";

const blank = () => ({
  check_date: todayISO(),
  ingredient_name: "",
  supplier: "",
  checked_by: "",
  linked_batch_id: "",
  temperature_c: "",
  temperature_status: "OK",
  packaging_condition: "OK",
  visual_quality: "OK",
  expiry_date: "",
  condition: "OK",
  issue_detected: "",
  action_taken: "",
  notes: "",
});

export default function RawMaterialChecks() {
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [fSupplier, setFSupplier] = useState("");
  const [fBatch, setFBatch] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const load = async () => {
    const [r, b] = await Promise.all([api.listRawMaterialChecks(), api.listProductionBatches()]);
    setItems(r);
    setBatches(b);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      const sup = (i.supplier || "").toLowerCase();
      const bat = (i.linked_batch_id || "").toLowerCase();
      const d = i.check_date || "";
      if (fSupplier && !sup.includes(fSupplier.toLowerCase())) return false;
      if (fBatch && !bat.includes(fBatch.toLowerCase())) return false;
      if (fFrom && d < fFrom) return false;
      if (fTo && d > fTo) return false;
      return true;
    });
  }, [items, fSupplier, fBatch, fFrom, fTo]);

  const save = async () => {
    if (!form.supplier || (!form.ingredient_name && !form.product))
      return toast.error("Ingredient and Supplier required");
    try {
      // Send temperature_c as number (or null if empty)
      const payload = {
        ...form,
        product: form.ingredient_name || form.product,
        temperature_c: form.temperature_c === "" ? null : Number(form.temperature_c),
        // Compute aggregate condition flag
        condition: (
          form.temperature_status === "Not OK" ||
          form.packaging_condition === "Damaged" ||
          form.visual_quality === "Not OK"
        ) ? "Issue" : "OK",
      };
      await api.createRawMaterialCheck(payload);
      toast.success("Raw material logged");
      setOpen(false);
      setForm(blank());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.deleteRawMaterialCheck(id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const downloadSingle = async (id) => {
    try { await api.downloadRawMaterialPdf(id); toast.success("PDF downloaded"); }
    catch { toast.error("PDF failed"); }
  };

  const downloadRange = async () => {
    try {
      const params = {};
      if (fFrom) params.from_date = fFrom;
      if (fTo) params.to_date = fTo;
      if (fSupplier) params.supplier = fSupplier;
      if (fBatch) params.batch_id = fBatch;
      await api.downloadRawMaterialRangePdf(params);
      toast.success("Range PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No matching records");
    }
  };

  return (
    <div data-testid="page-raw-material">
      <PageHeader
        overline="Omavalvonta · Food Safety"
        title="Raw Material Reception"
        subtitle="Track every incoming ingredient. Temperature + traceability."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadRange} data-testid="download-rm-range">
              <FileDown size={16} className="mr-2" /> Export Range PDF
            </Button>
            <Button onClick={() => { setForm(blank()); setOpen(true); }} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="add-raw-material">
              <Plus size={16} className="mr-2" /> New Entry
            </Button>
          </div>
        }
      />

      <div className="card-soft p-4 mb-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
          <Input value={fSupplier} onChange={(e) => setFSupplier(e.target.value)} placeholder="Supplier" className="pl-9 text-xs" data-testid="rm-filter-supplier" />
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
          <Input value={fBatch} onChange={(e) => setFBatch(e.target.value)} placeholder="Batch" className="pl-9 font-mono text-xs" data-testid="rm-filter-batch" />
        </div>
        <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="text-xs" data-testid="rm-filter-from" />
        <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="text-xs" data-testid="rm-filter-to" />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Wheat} title="No raw material entries" description="Log every incoming delivery for full traceability." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((i) => {
            const tempBad = i.temperature_status === "Not OK";
            const pkgBad = i.packaging_condition === "Damaged";
            const visBad = i.visual_quality === "Not OK";
            const anyIssue = tempBad || pkgBad || visBad || i.condition === "Issue";
            const ing = i.ingredient_name || i.product || "—";
            return (
              <div key={i.id} className="card-soft p-5 flex flex-col gap-2" data-testid={`rm-card-${i.id}`}>
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#6C757D] font-bold">{formatDateEU(i.check_date)}</div>
                    <div className="font-display font-bold text-lg text-[#222] truncate">{ing}</div>
                    <div className="text-xs text-[#6C757D] truncate">from {i.supplier}</div>
                    {i.linked_batch_id && <div className="font-mono text-xs text-[#E85D04] font-bold">→ {i.linked_batch_id}</div>}
                  </div>
                  <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${anyIssue ? "bg-[#D62828]/10 text-[#D62828]" : "bg-[#1F4D3A]/10 text-[#1F4D3A]"}`}>
                    {anyIssue ? "ISSUE" : "OK"}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {i.temperature_c !== null && i.temperature_c !== undefined && (
                    <div className="flex items-center gap-1.5 text-[#222]">
                      <Thermometer size={12} className={tempBad ? "text-[#D62828]" : "text-[#1F4D3A]"} />
                      <span className={`font-bold ${tempBad ? "text-[#D62828]" : "text-[#1F4D3A]"}`}>{i.temperature_c} °C</span>
                    </div>
                  )}
                  {i.expiry_date && (
                    <div className="text-[#6C757D]">Exp: {formatDateEU(i.expiry_date)}</div>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-1 text-[10px]">
                  <Pill ok={!tempBad} label={`Temp ${i.temperature_status || "—"}`} />
                  <Pill ok={!pkgBad} label={i.packaging_condition || "—"} />
                  <Pill ok={!visBad} label={`Vis ${i.visual_quality || "—"}`} />
                </div>
                {i.issue_detected && (
                  <div className="text-xs bg-[#D62828]/5 border border-[#D62828]/20 rounded-md p-2">
                    <span className="text-[10px] uppercase tracking-wider text-[#D62828] font-bold">Issue:</span> {i.issue_detected}
                  </div>
                )}
                <div className="text-[10px] text-[#6C757D]">By {i.checked_by || i.created_by || "—"}</div>
                <div className="flex justify-end gap-1 border-t border-[#E5E5E5] pt-2 mt-1">
                  <Button variant="ghost" size="sm" onClick={() => downloadSingle(i.id)} className="text-[#1F4D3A]" data-testid={`pdf-rm-${i.id}`}>
                    <FileDown size={14} className="mr-1" /> PDF
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => del(i.id)} className="text-[#E76F51] hover:bg-[#E76F51]/10">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rm-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">New Raw Material Reception</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Delivery Date">
              <Input type="date" value={form.check_date} onChange={(e) => setForm({ ...form, check_date: e.target.value })} data-testid="rm-date" />
            </Field>
            <Field label="Checked By">
              <Input value={form.checked_by} onChange={(e) => setForm({ ...form, checked_by: e.target.value })} placeholder="Name" data-testid="rm-by" />
            </Field>
            <Field label="Ingredient Name *">
              <Input value={form.ingredient_name} onChange={(e) => setForm({ ...form, ingredient_name: e.target.value })} placeholder="e.g. Habanero chiles 5kg" data-testid="rm-ingredient" />
            </Field>
            <Field label="Supplier *">
              <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="e.g. Chiles Mexico SA" data-testid="rm-supplier" />
            </Field>
            <Field label="Linked Batch">
              <Select value={form.linked_batch_id || "none"} onValueChange={(v) => setForm({ ...form, linked_batch_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="rm-batch"><SelectValue placeholder="Select batch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {batches.slice(0, 30).map((b) => (
                    <SelectItem key={b.id} value={b.batch_id}>{b.batch_id} · {b.product}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Expiry Date">
              <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="rm-expiry" />
            </Field>

            <div className="col-span-2 pt-2 border-t border-[#E5E5E5]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#1F4D3A] font-bold mb-2 flex items-center gap-2">
                <Thermometer size={12} /> Temperature Control
              </div>
            </div>
            <Field label="Temperature at reception (°C)">
              <Input type="number" step="0.1" value={form.temperature_c} onChange={(e) => setForm({ ...form, temperature_c: e.target.value })} placeholder="e.g. 4.2" data-testid="rm-temp" />
            </Field>
            <Field label="Temperature Status">
              <OkNotOk value={form.temperature_status} onChange={(v) => setForm({ ...form, temperature_status: v })} testId="rm-temp-status" />
            </Field>

            <div className="col-span-2 pt-2 border-t border-[#E5E5E5]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#1F4D3A] font-bold mb-2">Condition Check</div>
            </div>
            <Field label="Packaging">
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={form.packaging_condition === "OK" ? "default" : "outline"}
                  className={form.packaging_condition === "OK" ? "bg-[#1F4D3A] text-white flex-1" : "flex-1"}
                  onClick={() => setForm({ ...form, packaging_condition: "OK" })} data-testid="rm-pkg-ok">OK</Button>
                <Button type="button" size="sm" variant={form.packaging_condition === "Damaged" ? "default" : "outline"}
                  className={form.packaging_condition === "Damaged" ? "bg-[#D62828] text-white flex-1" : "flex-1"}
                  onClick={() => setForm({ ...form, packaging_condition: "Damaged" })} data-testid="rm-pkg-damaged">Damaged</Button>
              </div>
            </Field>
            <Field label="Visual Quality">
              <OkNotOk value={form.visual_quality} onChange={(v) => setForm({ ...form, visual_quality: v })} testId="rm-visual" />
            </Field>

            <Field label="Issue Detected" className="col-span-2">
              <Textarea rows={2} value={form.issue_detected} onChange={(e) => setForm({ ...form, issue_detected: e.target.value })} data-testid="rm-issue" />
            </Field>
            <Field label="Action Taken" className="col-span-2">
              <Textarea rows={2} value={form.action_taken} onChange={(e) => setForm({ ...form, action_taken: e.target.value })} data-testid="rm-action" />
            </Field>
            <Field label="Notes" className="col-span-2">
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="rm-notes" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-rm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);

const OkNotOk = ({ value, onChange, testId }) => (
  <div className="flex gap-2">
    <Button type="button" size="sm" variant={value === "OK" ? "default" : "outline"}
      className={value === "OK" ? "bg-[#1F4D3A] text-white flex-1" : "flex-1"}
      onClick={() => onChange("OK")} data-testid={`${testId}-ok`}>OK</Button>
    <Button type="button" size="sm" variant={value === "Not OK" ? "default" : "outline"}
      className={value === "Not OK" ? "bg-[#D62828] text-white flex-1" : "flex-1"}
      onClick={() => onChange("Not OK")} data-testid={`${testId}-not-ok`}>Not OK</Button>
  </div>
);

const Pill = ({ ok, label }) => (
  <div className={`rounded px-1.5 py-1 text-center font-bold ${ok ? "bg-[#1F4D3A]/10 text-[#1F4D3A]" : "bg-[#D62828]/10 text-[#D62828]"}`}>
    {label}
  </div>
);
